import axios from 'axios'
import React, { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Alert, Button, Col, Container, Progress, Row } from 'reactstrap'
import { useAuth } from 'context/AuthContext'
import { toast } from 'react-toastify'
import { ToastOptionsDefault } from 'utils/toastOptions'
import { useUser } from '../context/UserContext'

const UploadPage: React.FC = () => {
  const { token } = useAuth()
  const { user } = useUser()
  const [ files, setFiles ] = useState<File[]>([])
  const [ error, setError ] = useState<string | null>(null)
  const [ uploading, setUploading ] = useState<boolean>(false)
  const [ progress, setProgress ] = useState<number>(0)
  const [ uploadSuccess, setUploadSuccess ] = useState<boolean | null>(null)
  
  const MAX_FILES = user?.isAdmin ? 42000 : 100
  const MAX_FILE_SIZE = (user?.isAdmin ? 1000 : 10) * 1024 * 1024 // 10 MB
  console.log('MAX_FILE_SIZE', MAX_FILE_SIZE)
  const onDrop = (acceptedFiles: File[], rejectedFiles: any[]) => {
    setError(null)
    setUploadSuccess(null)
    
    const validExtensions = [ 'jpg', 'jpeg', 'png' ]
    
    const validFiles = acceptedFiles.filter((file) => {
      const fileExtension = file.name.split('.').pop()?.toLowerCase()
      return validExtensions.includes(fileExtension || '') && file.size <= MAX_FILE_SIZE
    })
    
    const invalidFiles = acceptedFiles.filter((file) => {
      const fileExtension = file.name.split('.').pop()?.toLowerCase()
      return !validExtensions.includes(fileExtension || '') || file.size > MAX_FILE_SIZE
    })
    
    if (invalidFiles.length > 0) {
      const invalidFileNames = invalidFiles.map((file) => file.name).join(', ')
      setError(`The following files were rejected: ${ invalidFileNames }. Only JPG and PNG files under 10MB are allowed.`)
    }
    
    if (validFiles.length + files.length > MAX_FILES) {
      setError(`You can only upload up to ${ MAX_FILES } files at a time.`)
      return
    }
    
    setFiles((prevFiles) => [ ...prevFiles, ...validFiles ])
  }
  
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true
  })
  
  const handleUpload = async () => {
    setUploading(true)
    setProgress(0)
    
    const formData = new FormData()
    
    files.forEach((file) => {
      const fileNameWithoutExtension = file.name.split('.').slice(0, -1).join('.')
      formData.append('screenshots', file)
      formData.append('titles', fileNameWithoutExtension)
    })
    
    try {
      const response = await axios.post('/api/screenshot/upload', formData, {
        headers: {
          Authorization: `Bearer ${ token }`
        },
        onUploadProgress: (event) => {
          if (event.lengthComputable) {
            const percentage = Math.round((event.loaded * 100) / (event?.total ?? 1))
            setProgress(percentage)
          }
        },
        timeout: 0
      })
      
      if (response.status === 201) {
        toast.success(`${ files.length } images have been successfully uploaded ! You can now access them on your gallery page.`, ToastOptionsDefault)
        setUploadSuccess(true)
        setFiles([])
      } else {
        throw new Error(response.data.error || 'An error occurred during upload.')
      }
    } catch (error: any) {
      setError(error.message)
      setUploadSuccess(false)
      toast.error(error.message || 'An error occurred during upload.', ToastOptionsDefault)
    } finally {
      setUploading(false)
    }
  }
  
  return (
    <>
      <div className="position-relative">
        <section className="section section-hero section-shaped">
          <div className="shape shape-style-1 shape-default">
            <span className="span-150"/>
            <span className="span-50"/>
            <span className="span-50"/>
            <span className="span-75"/>
            <span className="span-100"/>
            <span className="span-75"/>
            <span className="span-50"/>
            <span className="span-100"/>
            <span className="span-50"/>
            <span className="span-100"/>
          </div>
          <Container className="mt-5">
            <h1>Manually upload images to my gallery</h1>
            <Row className="justify-content-center mt-4">
              <Col md="8">
                <div { ...getRootProps({ className: 'dropzone' }) } style={ dropzoneStyle }>
                  <input { ...getInputProps() } />
                  <p>Drag &apos;n&apos; drop some files here, or click to select files</p>
                  <p className="text-muted">You can upload up to { MAX_FILES } images, each no larger than 10MB.</p>
                </div>
                { error && <Alert color="danger" className="mt-3">{ error }</Alert> }
                { files.length > 0 && (
                  <div className="mt-3">
                    <h5>Files ready to upload:</h5>
                    <ul>
                      { files.map((file, index) => (
                        <li key={ index }>{ file.name } - { (file.size / 1024 / 1024).toFixed(2) } MB</li>
                      )) }
                    </ul>
                    { uploading ? (
                      <Progress value={ progress } className="mt-3"/>
                    ) : (
                      <Button color="primary" onClick={ handleUpload } disabled={ files.length === 0 }>
                        Upload { files.length } { files.length === 1 ? 'file' : 'files' }
                      </Button>
                    ) }
                  </div>
                ) }
                { uploadSuccess === true && <Alert color="success" className="mt-3">Upload successful !</Alert> }
                { uploadSuccess === false && <Alert color="danger" className="mt-3">Upload failed !</Alert> }
              
              </Col>
            </Row>
          </Container>
          <div className="separator separator-bottom separator-skew zindex-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
              version="1.1"
              viewBox="0 0 2560 100"
              x="0"
              y="0"
            >
              <polygon
                className="fill-white"
                points="2560 0 2560 100 0 100"
              />
            </svg>
          </div>
        </section>
      </div>
    </>
  )
}

const dropzoneStyle: React.CSSProperties = {
  border: '2px dashed #007bff',
  borderRadius: '5px',
  padding: '20px',
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: '#f8f9fa'
}

export default UploadPage
